import { Controller, useForm } from "react-hook-form";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Header from "../../components/Header/Header";
import SettingsSection from "../../components/SettingsSection";
import Switch from "../../../../ui/Switch/Switch";
import { z } from "zod";
import styles from "./ImageGeneration.module.scss";
import NumberInput from "../../../../ui/NumberInput/NumberInput";
import Label from "../../../../ui/Label/Label";
import Fieldset from "../../../../ui/Fieldset/Fieldset";
import Select from "../../../../ui/Select/Select";
import { graphql } from "../../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import { useMemo } from "react";
import { useToast } from "../../../../ui/Toast/ToastContext";
import Loader from "../../../../ui/Loader/Loader";
import useUser from "../../../../providers/UserProvider/useUser";
import { ImageProvider } from "../../../../__generated__/gql/graphql";
import Tooltip from "../../../../ui/Tooltip/Tooltip";
import { Help } from "../../../../ui/Icon/Icon";
import { zodResolver } from "@hookform/resolvers/zod";
import Feedback from "../../../../ui/Feedback/Feedback";
import classNames from "classnames";

const imageProviderOptions = {
  [ImageProvider.Unsplash]: "Unsplash",
  [ImageProvider.Freepik]: "Freepik",
  [ImageProvider.Pexels]: "Pexels",
};

const ImageGenerationSettingsQuery = graphql(/* GraphQL */ `
  query imageGenerationSettings {
    imageGenerationSettings {
      generateHeroImage
      generateArticleImages
      imagesPerArticle
      customImageSize {
        width
        height
      }
      defaultImageProvider
      allowAiImages
    }
  }
`);

const UpdateImageGenerationSettingsMutation = graphql(/* GraphQL */ `
  mutation updateImageGenerationSettings(
    $input: UpdateImageGenerationSettingsInput!
  ) {
    updateImageGenerationSettings(input: $input) {
      generateHeroImage
      generateArticleImages
      imagesPerArticle
      customImageSize {
        width
        height
      }
      defaultImageProvider
      allowAiImages
    }
  }
`);

const schema = z.object({
  generateHeroImage: z.boolean(),
  generateImagesInArticle: z.boolean(),
  imagesPerArticle: z.number().optional(),
  useCustomImageSize: z.boolean(),
  customImageSize: z
    .object({
      width: z.number().max(1920).min(1),
      height: z.number().max(1920).min(1),
    })
    .optional(),
  defaultImageProvider: z.nativeEnum(ImageProvider),
  allowAiImages: z.boolean().optional(),
});

type ImageGenerationFormData = z.infer<typeof schema>;

function ImageGenerationForm({
  defaultValues,
  onSubmit,
}: {
  defaultValues: ImageGenerationFormData;
  onSubmit: (data: ImageGenerationFormData) => void | Promise<void>;
}) {
  const { showToast } = useToast();
  const { billingInfo } = useUser();

  const imagesInBody = billingInfo.imagesPerArticleLimit - 1;

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<ImageGenerationFormData>({
    defaultValues,
    resolver: function (values, ...args) {
      if (!values.useCustomImageSize) {
        return zodResolver(schema)(
          { ...values, customImageSize: undefined },
          ...args,
        );
      }

      return zodResolver(schema)(values, ...args);
    },
  });

  const [useCustomImageSize, generateImagesInArticle, defaultImageProvider] =
    watch([
      "useCustomImageSize",
      "generateImagesInArticle",
      "defaultImageProvider",
    ]);

  const onSubmitWrapper = async (data: ImageGenerationFormData) => {
    await onSubmit(data);

    reset(data);

    showToast({
      title: "Image generation settings saved",
      variant: "success",
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmitWrapper)}>
      <UnsavedChangesModal isDirty={isDirty} />
      <Header
        title="Image Generation"
        description="Configure how Macaw generates images"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        onCancel={() => reset()}
      />
      <SettingsSection
        label="Image settings"
        description="Configure article tags or categories for Macaw to choose from when writing articles"
      >
        <div className={styles.inputs}>
          <Controller
            name="generateHeroImage"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>Generate hero image</span>
              </label>
            )}
          />
          <Controller
            name="generateImagesInArticle"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  onCheckedChange={field.onChange}
                  checked={field.value}
                  disabled={!imagesInBody}
                />
                <span className={styles.switchLabel}>
                  Generate images within article
                </span>
              </label>
            )}
          />
          {generateImagesInArticle && (
            <Controller
              name="imagesPerArticle"
              control={control}
              render={({ field }) => (
                <Label
                  label="Number of images per article"
                  tooltip="Macaw will aim to add this number of images. This is separate to the hero image."
                >
                  <Select
                    ref={field.ref}
                    onBlur={field.onBlur}
                    value={field.value?.toString()}
                    onChange={(e) => field.onChange(e ? parseInt(e, 10) : null)}
                    data={Array.from({ length: imagesInBody }, (v, i) => ({
                      value: (i + 1).toString(),
                      label: (i + 1).toString(),
                    }))}
                  />
                </Label>
              )}
            />
          )}
          <Controller
            name="useCustomImageSize"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>Custom image size</span>
              </label>
            )}
          />
          {useCustomImageSize && (
            <Fieldset
              legend="Image size"
              tooltip="Recommend image size: 1200x800"
            >
              <div
                className={classNames(styles.inputRow, styles.inputRowImage)}
              >
                <Controller
                  name="customImageSize.width"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Label label="Image width" hideLabel>
                      <NumberInput
                        ref={field.ref}
                        onBlur={field.onBlur}
                        value={field.value?.toString()}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value))
                        }
                        rightSection={
                          <div className={styles.inputSuffix}>
                            Width (Pixels)
                          </div>
                        }
                      />
                      {error?.message && <Feedback>{error.message}</Feedback>}
                    </Label>
                  )}
                />
                <Controller
                  name="customImageSize.height"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Label label="Image height" hideLabel>
                      <NumberInput
                        ref={field.ref}
                        onBlur={field.onBlur}
                        value={field.value?.toString()}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value))
                        }
                        rightSection={
                          <div className={styles.inputSuffix}>
                            Height (Pixels)
                          </div>
                        }
                      />
                      {error?.message && <Feedback>{error.message}</Feedback>}
                    </Label>
                  )}
                />
              </div>
            </Fieldset>
          )}
        </div>
      </SettingsSection>
      <SettingsSection
        label="Default image service"
        description="Choose the image service that is used for selecting images during article generation"
      >
        <div className={styles.inputs}>
          <Controller
            control={control}
            name="defaultImageProvider"
            render={({ field }) => (
              <Select
                data={Object.keys(imageProviderOptions).map((value) => ({
                  value,
                  label:
                    imageProviderOptions[
                      value as keyof typeof imageProviderOptions
                    ],
                }))}
                onBlur={field.onBlur}
                onChange={(value) => field.onChange(value)}
                ref={field.ref}
                value={field.value}
              />
            )}
          />
          {defaultImageProvider === ImageProvider.Freepik && (
            <Controller
              name="allowAiImages"
              control={control}
              render={({ field }) => (
                <label className={styles.switchContainer} htmlFor={field.name}>
                  <Switch
                    id={field.name}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <span className={styles.switchLabel}>Allow AI images</span>

                  <Tooltip
                    content="Freepik has a library of human created and AI generated stock images. Sometimes AI generated images are of a lower quality, toggling this option off will prevent any AI generated images from being automatically selected by Macaw"
                    asChild={false}
                  >
                    <Help className={styles.icon} />
                  </Tooltip>
                </label>
              )}
            />
          )}
        </div>
      </SettingsSection>
    </form>
  );
}

function ImageGeneration() {
  const [imageGenerationSettingsResponse] = useQuery({
    query: ImageGenerationSettingsQuery,
  });
  const [, updateImageGenerationSettings] = useMutation(
    UpdateImageGenerationSettingsMutation,
  );
  const imageGenerationSettings =
    imageGenerationSettingsResponse.data?.imageGenerationSettings;
  const { billingInfo } = useUser();

  const defaultValues: ImageGenerationFormData = useMemo(() => {
    if (!imageGenerationSettings) {
      return {
        generateHeroImage: false,
        generateImagesInArticle: false,
        imagesPerArticle: 1,
        useCustomImageSize: false,
        customImageSize: {
          width: 1200,
          height: 800,
        },
        defaultImageProvider: ImageProvider.Unsplash,
      };
    }

    const {
      generateHeroImage,
      generateArticleImages,
      imagesPerArticle,
      customImageSize,
      defaultImageProvider,
    } = imageGenerationSettings;

    return {
      generateHeroImage: !!generateHeroImage,
      generateImagesInArticle:
        billingInfo?.imagesPerArticleLimit &&
        billingInfo.imagesPerArticleLimit > 1
          ? !!generateArticleImages
          : false,
      imagesPerArticle: imagesPerArticle || 2,
      useCustomImageSize: !!customImageSize,
      customImageSize: customImageSize || {
        width: 1200,
        height: 800,
      },
      defaultImageProvider: defaultImageProvider ?? ImageProvider.Unsplash,
    };
  }, [billingInfo, imageGenerationSettings]);

  const onSubmit = async (data: ImageGenerationFormData) => {
    await updateImageGenerationSettings({
      input: {
        imagesPerArticle: data.imagesPerArticle,
        generateHeroImage: data.generateHeroImage,
        generateArticleImages: data.generateImagesInArticle,
        customImageSize: data.useCustomImageSize ? data.customImageSize : null,
        defaultImageProvider: data.defaultImageProvider,
        allowAiImages: data.allowAiImages,
      },
    });
  };

  if (imageGenerationSettingsResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <ImageGenerationForm defaultValues={defaultValues} onSubmit={onSubmit} />
  );
}

export default ImageGeneration;
